
import NoResultsDisplay from './NoResultsDisplay';
import type { ParkingInApiResponse } from '@/services/dto/parking';
import type { ParkingRecord } from '@/services/dto/parking';
import SearchResultsList, { type SearchResultsListRef } from './SearchResultsList';
import { forwardRef, useImperativeHandle, useRef, useState, useEffect } from 'react';
import { ContentLoading } from '@/components/common/content-loading';

interface SearchResultsProps {
    searchQuery: string;
    onCardClick: (result: ParkingRecord) => void;
    parkingApiData?: ParkingInApiResponse | null;
    isLoading?: boolean;
}

export interface SearchResultsRef {
    focusResults: () => void;
}

const SearchResultsComponent = forwardRef<SearchResultsRef, SearchResultsProps>(({ searchQuery, onCardClick, parkingApiData, isLoading }, ref) => {
    const searchResultsListRef = useRef<SearchResultsListRef>(null);
    
    // Transform API data to ParkingResult format
    const transformApiDataToParkingResult = (apiData: ParkingRecord[]): ParkingRecord[] => {
        if (!apiData || !Array.isArray(apiData)) {
            return [];
        }

        return apiData.map((item: ParkingRecord) => ({
            ...item,
        }));
    };

    // Use API data if available, otherwise fall back to mock data
    const availableData = parkingApiData?.resultBody || [];

    // Transform data to consistent format - always return ParkingResult[]
    const transformedData: ParkingRecord[] = parkingApiData?.resultBody
        ? transformApiDataToParkingResult(parkingApiData.resultBody)
        : (availableData as unknown as ParkingRecord[]);

    // Filter results based on search query
    const filteredResults = transformedData.filter((result: ParkingRecord) => {
        return result.licenseplate.includes(searchQuery);
    });

    // Focus function that can be called directly
    const focusResults = () => {
        
        // Only focus when API is done loading
        if (!isLoading) {
            if (filteredResults.length === 0) {
                // Focus on NoResultsDisplay using multiple selectors
                const selectors = [
                    '[aria-label*="parking.noResults"]',
                    '[data-region-nav][data-region-order="2"]',
                    '[data-interactive][tabIndex="2"]'
                ];
                
                for (const selector of selectors) {
                    const element = document.querySelector(selector) as HTMLElement;
                    if (element && typeof element.focus === 'function') {
                        element.focus();
                        return;
                    }
                }
            } else {
                // Focus on SearchResultsList header - try multiple approaches
                
                // Try ref first
                if (searchResultsListRef.current?.focusHeader) {
                    searchResultsListRef.current.focusHeader();
                    return;
                }
                
                // Fallback to querySelector
                const headerSelectors = [
                    '[aria-label*="parking.searchResultsList"]',
                    '[data-region-nav][data-region-order="2"][data-interactive]'
                ];
                
                for (const selector of headerSelectors) {
                    const element = document.querySelector(selector) as HTMLElement;
                    if (element && typeof element.focus === 'function') {
                        element.focus();
                        return;
                    }
                }
            }
        }
    };

    // Focus when search is triggered but wait for API to complete
    const [shouldFocusOnComplete, setShouldFocusOnComplete] = useState(false);

    // Trigger focus when search completes
    useEffect(() => {
        if (shouldFocusOnComplete && !isLoading) {
            setTimeout(focusResults, 100);
            setShouldFocusOnComplete(false);
        }
    }, [isLoading, shouldFocusOnComplete]);

    // Expose focus method to parent component
    useImperativeHandle(ref, () => ({
        focusResults: () => {
            if (isLoading) {
                // If still loading, mark that we should focus when done
                setShouldFocusOnComplete(true);
            } else {
                // If not loading, focus immediately
                setTimeout(focusResults, 100);
            }
        }
    }));

    // Show loading state if API is loading
    if (isLoading) {
        return (
            <ContentLoading loading={isLoading} />
        );
    }

    return (
        <>
            {filteredResults.length === 0 ? (
                <div className="widescreen:rounded-[calc(12*var(--vh-unit))] widescreen:py-[calc(60*var(--vh-unit))] widescreen:px-[calc(80*var(--vw-unit))] flex w-full flex-col gap-[calc(24*var(--vh-unit))] h-full">
                    <div className="flex flex-col gap-[calc(16*var(--vh-unit))] flex-1 overflow-y-auto widescreen:overflow-y-hidden">
                        <div className="flex items-center justify-center flex-1">
                            <NoResultsDisplay />
                        </div>
                    </div>
                </div>
            ) : (
                <div className="widescreen:bg-[var(--color-bg-white)] widescreen:rounded-[calc(12*var(--vh-unit))] widescreen:border-[calc(1*var(--vh-unit))] widescreen:border-[var(--color-border-transparent)] widescreen:py-[calc(60*var(--vh-unit))] widescreen:px-[calc(80*var(--vw-unit))] flex w-full flex-col gap-[calc(24*var(--vh-unit))] h-full">
                    <div className="flex flex-col gap-[calc(16*var(--vh-unit))] flex-1 overflow-y-auto widescreen:overflow-y-hidden scrollbar-custom-gray-99938f scrollbar-w-4">
                        <SearchResultsList ref={searchResultsListRef} results={filteredResults} onCardClick={onCardClick} />
                    </div>
                </div>
            )}
        </>
    );
});

SearchResultsComponent.displayName = 'SearchResults';
export default SearchResultsComponent;
